<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useFormatRelativeTime } from '~/utils/formatRelativeTime'
import useProductStore from '~/stores/product'
import { PendoFeature } from '~/models/Pendo'
import useGradeString from '~/composables/useGradeString'

const props = defineProps<{
  item: BaseItem
  activityTimestamp?: number
  amountVisited?: number
  amountInGroup?: number
}>()

const emit = defineEmits<{ open: [payload: { item: BaseItem }] }>()
const { t } = useI18n()
const { format, formatTitle, formatDateTimeAttr } = useFormatRelativeTime()
const { createGradeString } = useGradeString()
const { getProductFromResource } = useProductStore()
const formattedTimestamp = computed<string | undefined>(() => format(props.activityTimestamp))
const datetimeAttr = computed<string | undefined>(() => formatDateTimeAttr(props.activityTimestamp))
const titleAttr = computed<string | undefined>(() => formatTitle(props.activityTimestamp))
const product = computed(() => getProductFromResource(props.item))

const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  return product.value.subjects?.length ? t(`metadata.subjects.${product.value.subjects[0]}`) : ''
})

const subtitle = computed(() => {
  const subject = subjectOrProductName.value
  const grades = createGradeString(props.item.grades)
  const parent = props.item.parentLocationName

  return [subject, grades, parent].filter(Boolean).join(' / ')
})

const visitCountText = computed(() => {
  return t('reports.labels.visitCount', {
    visited: props.amountVisited,
    total: props.amountInGroup
  })
})

function openCard() {
  emit('open', { item: props.item })
}
</script>

<template>
  <article
    class="group border-green-40 motion-safe:animate-fade-in-up relative flex min-h-36 w-full cursor-pointer overflow-hidden rounded-lg border bg-white transition-all duration-200 hover:border-green-50 hover:shadow-lg focus-visible:ring sm:h-40"
    role="button"
    tabindex="0"
    :data-pendo="PendoFeature.ActivityReports.Activity"
    @click="openCard"
    @keydown.enter.prevent="openCard"
    @keydown.space.prevent="openCard"
  >
    <div class="bg-gray-5 relative h-36 w-24 shrink-0 overflow-hidden sm:h-40 sm:w-40">
      <img
        v-if="props.item.thumbnail"
        :src="props.item.thumbnail"
        :alt="props.item.thumbnailAlt"
        class="absolute inset-0 size-full object-cover"
        loading="lazy"
      >
    </div>
    <div class="flex min-w-0 flex-1 flex-col p-2 sm:p-4">
      <div
        v-if="props.item.label || subjectOrProductName"
        class="order-first mb-1 flex flex-wrap items-center gap-1 sm:mb-2 sm:gap-2"
      >
        <span
          v-if="props.item.label"
          class="bg-seagreen-60 text-seagreen-5 rounded-md px-1.5 py-0.5 text-xs font-semibold sm:px-2 sm:py-1"
          v-text="t(`labels.${props.item.label}`)"
        />
        <span
          v-if="subjectOrProductName"
          class="bg-seagreen-60 text-seagreen-5 rounded-md px-1.5 py-0.5 text-xs font-semibold sm:px-2 sm:py-1"
          v-text="subjectOrProductName"
        />
      </div>

      <h3 class="mb-1 truncate text-sm font-extrabold text-gray-50 sm:mb-1 sm:text-lg sm:leading-tight" v-text="props.item.title" />

      <p
        v-if="!!subtitle"
        class="mb-1 truncate text-xs text-gray-50 sm:mb-2"
        v-text="subtitle"
      />

      <p
        v-if="props.item.intro"
        class="text-gray-70 mb-2 line-clamp-2 text-xs sm:mb-4 sm:text-base"
        v-text="props.item.intro"
      />

      <div class="mt-auto flex flex-col gap-1 pt-1 sm:flex-row sm:items-end sm:justify-between sm:gap-1 sm:pt-4">
        <p class="text-gray-60 text-xs leading-relaxed sm:text-sm">
          {{ visitCountText }}
        </p>

        <time
          v-if="formattedTimestamp"
          :datetime="datetimeAttr"
          class="text-gray-60 text-xs leading-relaxed"
          :title="titleAttr"
        >
          {{ formattedTimestamp }}
        </time>
      </div>
    </div>
  </article>
</template>
